import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { ChartLineUp } from "@phosphor-icons/react";
import { toast } from "sonner";

const LABELS = { expected: "Expected", best: "Best case", worst: "Worst case" };
const TONES = { expected: "border-l-[#1E5CBA]", best: "border-l-emerald-500", worst: "border-l-rose-500" };

export default function CashflowScenarios() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/finance/cashflow/scenarios", { params: { months: 3 } })
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);
  if (!data) return null;
  return (
    <div className="dc-card overflow-hidden" data-testid="cashflow-scenarios">
      <div className="border-b border-slate-100 px-5 py-3 flex flex-wrap items-center gap-2">
        <ChartLineUp size={18} weight="fill" style={{ color: "#1E5CBA" }} />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Scenario Forecast — Expected / Best / Worst</h3>
        <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
          Opening cash {formatCurrency(data.opening_cash)} · {data.horizon_months}-month horizon
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 p-4 lg:grid-cols-3">
        {Object.entries(data.scenarios).map(([k, s]) => (
          <div key={k} className={`rounded-md border border-slate-200 border-l-4 ${TONES[k]} p-3`} data-testid={`scenario-${k}`}>
            <div className="flex items-baseline justify-between">
              <div className="overline">{LABELS[k]}</div>
              <div className="text-[10px] text-slate-400">collect {Math.round((data.assumptions.collection_probability[k] || 0) * 100)}% AR</div>
            </div>
            <div className={`font-display text-xl font-bold tabular-nums ${s.closing_cash >= 0 ? "text-slate-900" : "text-rose-600"}`} data-testid={`scenario-${k}-closing`}>
              {formatCurrency(s.closing_cash)}
            </div>
            <div className="text-[11px] text-slate-500">
              net {formatCurrency(s.monthly_net)}/mo
              {s.runway_months != null && <> · runway <b>{s.runway_months} mo</b></>}
              {s.funding_gap < 0 && <> · <span className="font-semibold text-rose-600">gap {formatCurrency(s.funding_gap)}</span></>}
            </div>
            <table className="mt-2 w-full text-[11px]">
              <tbody>
                {s.months.map((m) => (
                  <tr key={m.period} className="border-t border-slate-100">
                    <td className="py-1 font-mono-alt">{m.period}</td>
                    <td className="text-right font-mono-alt tabular-nums text-emerald-700">+{formatCurrency(m.inflows)}</td>
                    <td className="text-right font-mono-alt tabular-nums text-rose-600">−{formatCurrency(m.outflows)}</td>
                    <td className={`text-right font-mono-alt tabular-nums font-semibold ${m.closing < 0 ? "text-rose-600" : ""}`}>{formatCurrency(m.closing)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-100 px-5 py-2 text-[10px] text-slate-400" data-testid="scenario-assumptions">
        Assumptions: AR outstanding {formatCurrency(data.assumptions.ar_outstanding)} · AP outstanding {formatCurrency(data.assumptions.ap_outstanding)} ·
        payroll avg {formatCurrency(data.assumptions.monthly_payroll_avg)}/mo · overhead avg {formatCurrency(data.assumptions.monthly_overhead_avg)}/mo (trailing {data.assumptions.trailing_periods.join(", ")})
      </div>
    </div>
  );
}
